import {
  BadgeTypeModel,
  ParticipantModel,
  ScanModel,
  ApiKeyModel,
  DashboardStats,
} from '../types';

export interface ParticipantEnriched extends ParticipantModel {
  badgeTypeName: string;
  badgeTypeColor: string;
}

export const api = {
  // Counters & Dashboard
  async getCounters(): Promise<DashboardStats> {
    const res = await fetch('/api/counters');
    if (!res.ok) throw new Error('Failed to load counters');
    return res.json();
  },

  // Badge Types
  async getBadgeTypes(): Promise<BadgeTypeModel[]> {
    const res = await fetch('/api/badge-types');
    if (!res.ok) throw new Error('Failed to load badge types');
    return res.json();
  },

  async createBadgeType(name: string, code?: string, color?: string): Promise<BadgeTypeModel> {
    const res = await fetch('/api/badge-types', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, code, color }),
    });
    if (!res.ok) throw new Error('Failed to create badge type');
    return res.json();
  },

  async updateBadgeType(id: string, data: Partial<BadgeTypeModel>): Promise<BadgeTypeModel> {
    const res = await fetch(`/api/badge-types/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Failed to update badge type');
    return res.json();
  },

  async deleteBadgeType(id: string): Promise<boolean> {
    const res = await fetch(`/api/badge-types/${id}`, { method: 'DELETE' });
    if (!res.ok) throw new Error('Failed to delete badge type');
    const data = await res.json();
    return data.success;
  },

  // Participants
  async getParticipants(): Promise<ParticipantEnriched[]> {
    const res = await fetch('/api/participants');
    if (!res.ok) throw new Error('Failed to load participants');
    return res.json();
  },

  async createParticipant(data: {
    fullName: string;
    badgeTypeId: string;
    email?: string;
    phone?: string;
    photoUrl?: string;
  }): Promise<ParticipantModel> {
    const res = await fetch('/api/participants', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Failed to create participant');
    return res.json();
  },

  async importParticipants(rows: any[]): Promise<{ imported: number; total: number }> {
    const res = await fetch('/api/participants/import', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ rows }),
    });
    if (!res.ok) throw new Error('Failed to import participants');
    return res.json();
  },

  async deleteParticipant(id: string): Promise<boolean> {
    const res = await fetch(`/api/participants/${id}`, { method: 'DELETE' });
    if (!res.ok) throw new Error('Failed to delete participant');
    const data = await res.json();
    return data.success;
  },

  async getParticipantBadge(idOrToken: string): Promise<{ eventName: string; participant: ParticipantModel; badgeType?: BadgeTypeModel }> {
    const res = await fetch(`/api/participants/${idOrToken}/badge`);
    if (!res.ok) throw new Error('Badge not found');
    return res.json();
  },

  // Scan Validation with Location
  async validateScan(secureQrToken: string, location?: string): Promise<{
    result: 'VALID' | 'INVALID_QR' | 'BADGE_DISABLED' | 'ALREADY_SCANNED';
    message: string;
    participant?: ParticipantModel;
    badgeType?: BadgeTypeModel;
    location?: string;
    previousScanTime?: string;
  }> {
    const res = await fetch('/api/scans/validate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ secureQrToken, location }),
    });
    if (!res.ok) throw new Error('Failed to validate scan');
    return res.json();
  },

  // API Keys
  async getApiKeys(): Promise<ApiKeyModel[]> {
    const res = await fetch('/api/api-keys');
    if (!res.ok) throw new Error('Failed to load API keys');
    return res.json();
  },

  async createApiKey(name: string, type: 'badge_type' | 'total', badgeTypeId?: string): Promise<{ apiKey: ApiKeyModel; rawKey: string }> {
    const res = await fetch('/api/api-keys', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, type, badgeTypeId }),
    });
    if (!res.ok) throw new Error('Failed to create API key');
    return res.json();
  },

  async deleteApiKey(id: string): Promise<boolean> {
    const res = await fetch(`/api/api-keys/${id}`, { method: 'DELETE' });
    if (!res.ok) throw new Error('Failed to delete API key');
    const data = await res.json();
    return data.success;
  },

  // Event info
  async getEventInfo(): Promise<{ eventName: string }> {
    const res = await fetch('/api/event-info');
    return res.json();
  },

  async updateEventInfo(eventName: string): Promise<{ eventName: string }> {
    const res = await fetch('/api/event-info', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ eventName }),
    });
    return res.json();
  },
};
